import { useMemo } from "react";
import { fetchErrors } from "../api";
import { usePolledData } from "../hooks/usePolledData";
import { fmtDate, fmtMs, fmtNum } from "../format";
import { Section } from "../components/Section";
import { DataTable, type ColumnDef } from "../components/DataTable";
import {
  ApiTypeTag,
  CircuitBadge,
  StatusBadge,
} from "../components/Badges";
import type {
  CircuitState,
  ErrorByApiRow,
  ErrorByCodeRow,
  ErrorsResponse,
  RecentErrorRow,
  TimeWindow,
} from "../types";

interface Props {
  window: TimeWindow;
  registerState: (state: {
    loading: boolean;
    lastUpdated: Date | null;
    refetch: () => void;
  }) => void;
}

export const ErrorsPage = ({ window, registerState }: Props) => {
  const { data, error, loading, lastUpdated, refetch } = usePolledData<ErrorsResponse>(
    () => fetchErrors(window),
    { intervalMs: 15_000, deps: [window] },
  );
  registerState({ loading, lastUpdated, refetch });

  const codeCols = useMemo<ColumnDef<ErrorByCodeRow>[]>(
    () => [
      { key: "code", header: "Error code", render: (r) => r._id || "—" },
      { key: "count", header: "Count", align: "right", render: (r) => fmtNum(r.count) },
    ],
    [],
  );

  const apiCols = useMemo<ColumnDef<ErrorByApiRow>[]>(
    () => [
      { key: "api", header: "API", render: (r) => <ApiTypeTag type={r._id} /> },
      { key: "count", header: "Errors", align: "right", render: (r) => fmtNum(r.count) },
      {
        key: "codes",
        header: "Distinct codes",
        render: (r) => (
          <span className="dim small">
            {(r.unique_codes || []).filter(Boolean).join(", ") || "—"}
          </span>
        ),
      },
    ],
    [],
  );

  const recentCols = useMemo<ColumnDef<RecentErrorRow>[]>(
    () => [
      { key: "time", header: "Time", render: (r) => fmtDate(r.created_at) },
      { key: "brand", header: "Brand", render: (r) => r.brand_id || "—" },
      {
        key: "api",
        header: "API",
        render: (r) => <ApiTypeTag type={r.api_type} />,
      },
      {
        key: "status",
        header: "Status",
        render: (r) => <StatusBadge code={r.response_status} />,
      },
      { key: "code", header: "Code", render: (r) => r.error_code || "—" },
      {
        key: "msg",
        header: "Message",
        render: (r) => (
          <span className="dim small clamp-2">{r.error_message || "—"}</span>
        ),
      },
      { key: "pan", header: "PAN", render: (r) => r.pan || "—" },
      {
        key: "dur",
        header: "Time",
        align: "right",
        render: (r) => fmtMs(r.response_time_ms),
      },
    ],
    [],
  );

  const cbCols = useMemo<ColumnDef<CircuitState>[]>(
    () => [
      { key: "name", header: "Provider", render: (r) => r.name },
      { key: "state", header: "State", render: (r) => <CircuitBadge state={r.state} /> },
      {
        key: "fails",
        header: "Failures",
        align: "right",
        render: (r) => `${fmtNum(r.failures)} / ${fmtNum(r.threshold)}`,
      },
      { key: "opened", header: "Opened at", render: (r) => fmtDate(r.opened_at) },
      {
        key: "cd",
        header: "Cooldown left",
        align: "right",
        render: (r) =>
          r.state === "OPEN" ? fmtMs(r.cooldown_ms_remaining) : "—",
      },
    ],
    [],
  );

  if (error) {
    return (
      <div className="error-banner">
        <strong>Failed to load errors:</strong> {error.message}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-2">
        <Section title="Top error codes">
          <DataTable
            columns={codeCols}
            rows={data?.by_code}
            rowKey={(r) => String(r._id ?? "null")}
            loading={loading}
            emptyMessage="No errors. ✨"
          />
        </Section>
        <Section title="Errors by API type">
          <DataTable
            columns={apiCols}
            rows={data?.by_api_type}
            rowKey={(r) => String(r._id)}
            loading={loading}
            emptyMessage="No errors. ✨"
          />
        </Section>
      </div>

      <Section
        title="Circuit breakers"
        description="Per-upstream state. OPEN = short-circuiting calls until cooldown elapses."
      >
        <DataTable
          columns={cbCols}
          rows={data?.circuits}
          rowKey={(r) => r.name}
          loading={loading}
          emptyMessage="No upstreams have been called yet."
        />
      </Section>

      <Section title="Recent failures (latest 50)">
        <DataTable
          columns={recentCols}
          rows={data?.recent}
          rowKey={(r) => r._id}
          loading={loading}
          emptyMessage="No errors. ✨"
        />
      </Section>
    </>
  );
};
